import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Mail, MapPin, Phone, Clock } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Placeholder } from "@/components/Placeholder";
import { BUDGET_OPTIONS, BUSINESS, CONTACT, SERVICES, SERVICE_OPTIONS } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls = "field mt-2";
const labelCls = "block text-sm font-medium text-ink";

const Field = ({ label, id, children, optional }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label} {optional && <span className="font-normal text-slate-400">(optional)</span>}
    </label>
    {children}
  </div>
);

const defaultService = (slug) => {
  const svc = SERVICES.find((s) => s.slug === slug);
  if (!svc) return "";
  return SERVICE_OPTIONS.find((o) => o.startsWith(svc.title.split(" /")[0])) || "";
};

const INITIAL = { name: "", email: "", phone: "", company: "", service: "", requirements: "", budget: "", message: "", consent: false };

const ContactForm = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...INITIAL, service: defaultService(params.get("service")) });
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please confirm you have read the Privacy Policy before submitting.");
      return;
    }
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail?.[0]?.msg || body.detail || "Something went wrong. Please try again or email us directly.");
      }
      const data = await res.json();
      setReference(data.reference);
      setStatus("success");
      toast.success("Enquiry received. We'll be in touch.");
    } catch (err) {
      setStatus("idle");
      setError(err.message);
      toast.error("Could not send your enquiry.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success" className="card border-forest/30 bg-teal-50 p-10">
        <p className="eyebrow text-forest">Received</p>
        <h3 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">Thank you — your request is logged.</h3>
        <p className="mt-4 text-sm leading-relaxed text-slate-700 md:text-base">
          Reference <span data-testid="contact-reference" className="rounded bg-white px-2 py-0.5 font-mono font-semibold text-ink">{reference}</span>. A confirmation email has been sent to{" "}
          <span className="font-medium text-ink">{form.email}</span>, and we will reply <Placeholder field={BUSINESS.responseTime} />.
          For anything urgent, call {CONTACT.phone}.
        </p>
        <button
          type="button"
          data-testid="contact-reset-button"
          onClick={() => { setForm(INITIAL); setStatus("idle"); }}
          className="btn-outline mt-8"
        >
          Send Another Enquiry
        </button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="card p-6 md:p-10">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Full Name" id="name">
          <input data-testid="contact-name-input" id="name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" autoComplete="name" />
        </Field>
        <Field label="Email" id="email">
          <input data-testid="contact-email-input" id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@company.com" autoComplete="email" />
        </Field>
        <Field label="Phone" id="phone" optional>
          <input data-testid="contact-phone-input" id="phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 000-0000" autoComplete="tel" />
        </Field>
        <Field label="Company" id="company">
          <input data-testid="contact-company-input" id="company" required value={form.company} onChange={set("company")} className={inputCls} placeholder="Company or trading name" autoComplete="organization" />
        </Field>
        <Field label="Service Interested In" id="service">
          <select data-testid="contact-service-select" id="service" required value={form.service} onChange={set("service")} className={`${inputCls} cursor-pointer`}>
            <option value="" disabled>Select a service…</option>
            {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Budget Range" id="budget" optional>
          <select data-testid="contact-budget-select" id="budget" value={form.budget} onChange={set("budget")} className={`${inputCls} cursor-pointer`}>
            <option value="">Select a range…</option>
            {BUDGET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <div className="mt-6">
        <Field label="Project Requirements" id="requirements">
          <textarea data-testid="contact-requirements-input" id="requirements" required rows={3} value={form.requirements} onChange={set("requirements")} className={`${inputCls} resize-none`} placeholder="What do you need built, improved or organised? Pages, features, timelines…" />
        </Field>
      </div>
      <div className="mt-6">
        <Field label="Message" id="message">
          <textarea data-testid="contact-message-input" id="message" required rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Anything else we should know — current website, deadlines, how you'd like us to reach you." />
        </Field>
      </div>

      <label data-testid="contact-consent-label" className="mt-8 flex items-start gap-3 rounded-lg bg-cream p-4 text-xs leading-relaxed text-slate-600">
        <input data-testid="contact-consent-checkbox" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 shrink-0 rounded accent-brand" />
        <span>
          I agree that {CONTACT.name} may store the information in this form and use it to respond to my enquiry and prepare a quotation, as described in the{" "}
          <Link to="/privacy-policy" className="font-medium text-brand hover:underline">Privacy Policy</Link>. I understand I can request deletion of my information at any time.
        </span>
      </label>

      {error && (
        <p data-testid="contact-error" role="alert" className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      <button
        type="submit"
        data-testid="contact-submit-button"
        disabled={status === "loading"}
        className="btn-primary mt-8 w-full sm:w-auto"
      >
        {status === "loading" ? "Sending…" : "Request a Quote"}
        {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
      </button>
    </form>
  );
};

const Detail = ({ icon: Icon, label, children, testId }) => (
  <div className="flex gap-4 border-b border-slate-200 py-5 last:border-b-0">
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand"><Icon size={16} /></span>
    <div>
      <p className="label-xs">{label}</p>
      <div data-testid={testId} className="mt-1 text-sm leading-relaxed text-ink">{children}</div>
    </div>
  </div>
);

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Seo title="Contact & Request a Quote" description="Contact My Geeks LLC to request a written quotation for website development, business management, SEO/SMO or digital marketing services." />
      <PageHeader
        overline="Contact / Request a quote"
        title="Tell us what you need"
        lede="Complete the form and we will reply with next steps and, where possible, a written quotation. Prefer to talk? Call or email during business hours."
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <ContactForm />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div data-testid="contact-details" className="card px-6">
              <Detail icon={Mail} label="Email" testId="contact-detail-email">
                <a href={`mailto:${CONTACT.email}`} className="font-medium text-brand hover:underline">{CONTACT.email}</a>
              </Detail>
              <Detail icon={Phone} label="Telephone" testId="contact-detail-phone">
                <a href={CONTACT.phoneHref} className="font-medium text-brand hover:underline">{CONTACT.phone}</a>
              </Detail>
              <Detail icon={MapPin} label="Business Address" testId="contact-detail-address">{CONTACT.address}, {CONTACT.country}</Detail>
              <Detail icon={Clock} label="Business Hours" testId="contact-detail-hours"><Placeholder field={BUSINESS.hours} /></Detail>
              <Detail icon={Clock} label="Expected Response Time" testId="contact-detail-response"><Placeholder field={BUSINESS.responseTime} /></Detail>
            </div>
            <div className="mt-6 rounded-xl border-l-4 border-brand bg-brand-soft p-6 text-sm leading-relaxed text-slate-600">
              <p className="font-semibold text-ink">How your information is handled</p>
              <p className="mt-2">
                Form submissions are stored securely and used only to respond to your request and prepare a quotation. A copy is emailed to {CONTACT.email} and a confirmation with your reference number is sent to you. We do not sell or share your details for marketing. See the{" "}
                <Link to="/privacy-policy" className="font-medium text-brand hover:underline">Privacy Policy</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
